import { RefObject } from "react";

import { BookIcon } from "@vericus/cadmus-icons";
import { Button, Pill, Text } from "@vericus/cadmus-ui";

import { NavLink, useParams } from "react-router-dom";

import CadmusLogo from "@/assets/cadmus-logo.svg";
import {
  AssessmentSettingsFragment,
  PeerEvaluationMode,
  SelfEvaluationMode,
} from "@/generated/graphql";
import { useAssessmentFragment } from "@/graphql/hooks";
import { ManualWithMutations } from "@/ui/shared/ManualWithMutations";

import * as styles from "./page-heading.css";
import { Settings } from "./settings";

interface Props {
  manualMountElement?: RefObject<HTMLDivElement>;
}

export function RootHeading(props: Pick<Props, "manualMountElement">) {
  const { manualMountElement } = props;
  const { assessmentId = "", tenant = "" } = useParams();

  const { data: assessment, complete } = useAssessmentFragment();
  const pills = complete ? settingPills(assessment.settings) : [];
  const unitWeight = complete ? unitWeighting(assessment.settings) : null;

  return (
    <div className={styles.root}>
      <header className={styles.rootHeader}>
        <NavLink
          to={`/${tenant}/${assessmentId}/home`}
          style={{ display: "flex" }}
        >
          <img className={styles.logo} src={CadmusLogo} alt="" />
        </NavLink>
        <div />
        <div className={styles.right}>
          <Settings />
          {manualMountElement && (
            <ManualWithMutations
              mountElement={manualMountElement}
              trigger={
                <Button
                  kind="ghost"
                  size="sm"
                  marginLeft={8}
                  rightIcon={<BookIcon label="" size={24} />}
                >
                  CADMUS MANUAL
                </Button>
              }
            />
          )}
        </div>
      </header>
      <div className={styles.title}>
        <div className={styles.titleText}>
          <Text kind="displayTwo">{assessment.name}</Text>
          <Text kind="label">{assessment.subjectName}</Text>
        </div>
        <div className={styles.weighting}>
          {unitWeight != null && (
            <Text kind="label" className={styles.unitWeight} as="div">
              {`Worth ${unitWeight}% of the total unit grade`}
            </Text>
          )}
          {/*
           * A lone pill weighs the assessment's only component against
           * nothing, and students read its "100%" as the assessment's share of
           * the unit. That is the confusion the line above it exists to clear
           * up, so weights appear only once there is a split to show.
           */}
          {pills.length > 1 && (
            <div className={styles.pills}>
              {pills.map((pill, index) => (
                <Pill basic key={index}>
                  {pill}
                </Pill>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Share of the unit this assessment carries, or `null` when the lecturer has
 * not set one.
 *
 * Distinct from the pills beside it, which weight the components against each
 * other rather than against the unit.
 */
function unitWeighting(
  settings: AssessmentSettingsFragment | null
): number | null {
  if (settings == null || settings.weightEnabled !== true) return null;
  return settings.weight ?? null;
}

function settingPills(settings: AssessmentSettingsFragment | null): string[] {
  const pills: string[] = [];
  if (!settings) return pills;
  // Only group assessments carry a group report. Gate on
  // `groupAssessmentEnabled` (not just a non-null weight) so a non-group
  // assessment never surfaces a "Group Work" pill — a 0% group weight is a
  // valid, intentional configuration that should still show.
  if (settings.groupAssessmentEnabled && settings.groupReportWeight != null) {
    pills.push(`Group Work: ${settings.groupReportWeight}%`);
  }
  if (settings.individualReportEnabled) {
    pills.push(`Individual Work: ${settings.individualReportWeight}%`);
  }
  // In scaling mode peer evaluation acts as a score multiplier on the group
  // report rather than carrying its own weight, so mirror Athena and label it
  // accordingly instead of showing a meaningless 0%.
  const peerScaling =
    settings.peerEvaluationEnabled &&
    settings.peerEvaluationMode === PeerEvaluationMode.Scaling;
  if (settings.peerEvaluationEnabled) {
    pills.push(
      `Peer Evaluation: ${
        peerScaling ? "Score multiplier" : `${settings.peerEvaluationWeight}%`
      }`
    );
  }
  if (settings.selfEvaluationEnabled) {
    // In :peerScaling mode self evaluation rides the peer multiplier and has no
    // standalone weight, so mirror Athena and label it a score multiplier
    // rather than a meaningless weight.
    const selfScaling =
      settings.selfEvaluationMode === SelfEvaluationMode.PeerScaling;
    const selfValue = selfScaling
      ? "Score multiplier"
      : settings.selfEvaluationWeight != null
        ? `${settings.selfEvaluationWeight}%`
        : "Enabled";
    pills.push(`Self Evaluation: ${selfValue}`);
  }
  return pills;
}
